import type { ReactNode } from "react";
import styles from "./ReviewPage.module.css";

const LABELS: Record<string, string> = {
  raw_name: "Name as written",
  lists: "Found in",
  candidates_tried: "Names tried",
  method: "Matched by",
  matched: "Matched",
  substance: "Matched substance",
  directpcw_cas: "Listing’s CAS",
  scentree_cas: "ScenTree’s CAS",
  pcw_code: "PCW product code",
  scentree_product: "ScenTree product",
  scentree_ingredient: "ScenTree ingredient",
  cas_votes: "CAS numbers given by partners",
  values: "CAS numbers found",
  url: "Page",
  source: "Source",
  path: "Odor path as published",
  slug: "ScenTree page",
  cas: "CAS as published",
  query: "Searched for",
  cids: "PubChem compounds",
  cid: "PubChem compound",
  title: "PubChem title",
  problem: "Problem",
  reported_formulas: "Formulas reported by sources",
  reported_mw: "Molecular weights reported",
  inchikey: "InChIKey",
};

const LIST_NAMES: Record<string, string> = {
  "fraterworks_order_oct11_2026.txt": "Fraterworks order of Oct 11",
  "jce_list.txt": "JCE list",
  "reddit_beginner_material_list.txt": "Reddit beginner list",
};

const METHODS: Record<string, string> = {
  material_name: "an existing material’s name",
  substance_name: "a substance name",
  trade_graph: "the curated trade-name graph",
  tgsc_index_name: "a TGSC index entry",
  natural_subset: "a natural whose name contains every word",
  pubchem_name: "a unique PubChem name match",
};

const SKIPPED = new Set(["suggestions", "properties", "options"]);

function isUrl(value: string): boolean {
  return /^https?:\/\//.test(value);
}

function renderValue(key: string, value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") return <span className={styles.subtle}>—</span>;
  if (key === "lists" && Array.isArray(value)) {
    return value.map((item) => LIST_NAMES[String(item)] ?? String(item)).join(", ");
  }
  if (key === "method" && typeof value === "string") return METHODS[value] ?? value.replaceAll("_", " ");
  if (key === "path" && Array.isArray(value)) return value.map(String).join(" › ");
  if (key === "cas_votes" && value && typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value as Record<string, unknown>)
      .map(([cas, count]) => `${cas} (${String(count)})`)
      .join(", ");
  }
  if (typeof value === "string") {
    if (isUrl(value)) {
      return (
        <a href={value} target="_blank" rel="noreferrer" className={styles.breakAnywhere}>
          {value}
        </a>
      );
    }
    return value.includes("|") ? value.split("|").join(", ") : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value))
    return value.map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item))).join(", ");
  return <code className={styles.code}>{JSON.stringify(value)}</code>;
}

/** The evidence a pipeline attached to a review item, as labelled facts. */
export function EvidenceList({ evidence }: { evidence: Record<string, unknown> }) {
  const entries = Object.entries(evidence).filter(([key]) => !SKIPPED.has(key));
  if (!entries.length) return <p className={styles.subtle}>No evidence was attached.</p>;
  return (
    <dl className={styles.evidence}>
      {entries.map(([key, value]) => (
        <div key={key} className={styles.evidenceRow}>
          <dt>{LABELS[key] ?? key.replaceAll("_", " ")}</dt>
          <dd>{renderValue(key, value)}</dd>
        </div>
      ))}
    </dl>
  );
}
